import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import {
  setMetricFinderHolder,
  setMetricFinderLoader,
  setMetricFinderMetrics
} from '@core/store/actions/metric-finder.actions'
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators'
import { Store } from '@ngrx/store'
import {
  fetchFavTeamComparisonMetricFinderMetrics,
  fetchFavTeamMetricFinderMetrics,
  fetchTeamComparisonMetricFinderMetrics,
  fetchTeamMetricFinderData,
  fetchTeamMetricFinderHolder,
  fetchTeamMetricFinderMetrics
} from '@core/store/actions/team-metric-finder.actions'
import { TeamApi } from '@core/requests/api/team/team.api'
import { buildTeamComparisonQuery, mapTeamSummary } from '@pages/team/utils/team.utils'
import { requestErrorHandler } from '@shared/operators/request-error.operator'
import { selectMergedRoute } from '@core/ngrx-router/state/selectors/ngrx-router.selectors'
import { buildTeamComparisonHolder } from '@core/utils/team.utils'
import { EMPTY } from 'rxjs'
import {
  selectAggregation,
  selectAggregationsLeftAggregation,
  selectAggregationsRightAggregation
} from '@features/aggregations/store/selectors/aggregations.selectors'
import { TeamHandler } from '@core/services/team/team.handler'
import { selectFavCurrentSeasonCompetitionWithFavTeamId } from '@features/seasons/state/selectors/seasons.selectors'

@Injectable()
export class TeamMetricFinderEffects {
  private readonly _actions$: Actions = inject(Actions)
  private readonly _store: Store = inject(Store)
  private readonly _teamApi: TeamApi = inject(TeamApi)
  private readonly _teamHandler: TeamHandler = inject(TeamHandler)

  fetchTeamMetricFinderData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchTeamMetricFinderData),
      concatLatestFrom(() => this._store.select(selectMergedRoute)),
      map(([, { params }]) => ({ ...params }) as any),
      map(({ vsCompetitionId, vsTeamId, seasonId }) => {
        const isVsEnabled = !!vsCompetitionId && !!vsTeamId
        if (isVsEnabled) {
          return fetchFavTeamComparisonMetricFinderMetrics({
            seasonIdB: seasonId,
            competitionIdB: vsCompetitionId,
            teamIdB: vsTeamId
          })
        } else {
          return fetchFavTeamMetricFinderMetrics()
        }
      })
    )
  )

  fetchTeamMetricFinderHolder$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchTeamMetricFinderHolder),
      switchMap(({ season, competition, teamId }) =>
        this._teamApi.fetchTeam(season.id, competition.id, teamId).pipe(
          map((team) => ({ ...team, season, competition })),
          requestErrorHandler()
        )
      ),
      map((team) => mapTeamSummary(team)),
      map((metricHolder) => setMetricFinderHolder({ metricHolder: { team: metricHolder } }))
    )
  )

  fetchTeamMetricFinderMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchTeamMetricFinderMetrics),
      tap(() => this._store.dispatch(setMetricFinderLoader({ loading: true }))),
      concatLatestFrom(() => [
        this._store.select(selectAggregationsLeftAggregation),
        this._store.select(selectAggregationsRightAggregation)
      ]),
      switchMap(([{ seasonId, competitionId, teamId }, leftAggregation, rightAggregation]) =>
        this._teamApi.fetchTeamMetrics(seasonId, competitionId, teamId, leftAggregation.id, rightAggregation.id).pipe(
          catchError((err) => {
            console.error('[ERROR] unable fetchTeamMetricFinderMetrics', err)
            this._store.dispatch(setMetricFinderLoader({ loading: false }))
            return EMPTY
          })
        )
      ),
      switchMap((metrics) => [setMetricFinderMetrics({ metrics }), setMetricFinderLoader({ loading: false })])
    )
  )

  fetchFavTeamMetricFinderMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchFavTeamMetricFinderMetrics),

      switchMap(() => this._teamHandler.defaultTeam$),
      mergeMap(({ season, competition, teamId }) => [
        fetchTeamMetricFinderHolder({ season, competition, teamId }),
        fetchTeamMetricFinderMetrics({ seasonId: season.id, competitionId: competition.id, teamId })
      ])
    )
  )

  fetchFavTeamComparisonMetricFinderMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchFavTeamComparisonMetricFinderMetrics),
      switchMap(({ type: _, ...params }) =>
        this._store.pipe(selectFavCurrentSeasonCompetitionWithFavTeamId('TeamSpace')).pipe(
          take(1),
          map((data) => ({ ...data, ...params }))
        )
      ),
      filter(({ teamId, ...params }) => !!teamId && !!params),
      concatLatestFrom(() => [this._store.select(selectAggregation)]),
      map(([{ season, competition, teamId, ...params }, aggregation]) =>
        buildTeamComparisonQuery({ competition, season, teamId, ...params }, aggregation.id)
      ),
      map((toCompare) => fetchTeamComparisonMetricFinderMetrics({ toCompare }))
    )
  )

  fetchTeamComparisonMetricFinderMetrics$ = createEffect(() =>
    this._actions$.pipe(
      ofType(fetchTeamComparisonMetricFinderMetrics),
      tap(() => this._store.dispatch(setMetricFinderLoader({ loading: true }))),
      switchMap(({ toCompare }) =>
        this._teamApi.compareTeams(toCompare).pipe(
          map(({ teamA, teamB, ...metrics }) => ({
            ...metrics,
            teamA: { ...teamA, ...toCompare.teamA },
            teamB: { ...teamB, ...toCompare.teamB }
          })),
          catchError((err) => {
            console.error('[ERROR] unable fetchTeamComparisonMetricFinderMetrics', err)
            this._store.dispatch(setMetricFinderLoader({ loading: false }))
            return EMPTY
          })
        )
      ),
      mergeMap(({ teamA, teamB, ...metrics }) => [
        setMetricFinderHolder({ metricHolder: { comparison: buildTeamComparisonHolder({ teamA, teamB }) } }),
        setMetricFinderMetrics({ metrics }),
        setMetricFinderLoader({ loading: false })
      ])
    )
  )
}
